// La marque Scorofun — identité définitive (fondateur, 12/08/2026).
//
//   symbole   « +1 »          branding/v2/logo-v3/, concept 2 — le geste du produit,
//                             quatre capsules blanches pleines dans une pastille.
//   wordmark  « a — Rondeur » branding/v2/logo-v3/wordmarks/ — géométrique arrondi,
//                             bols quasi circulaires, terminaisons en demi-rond.
//   couleur   signature c     framboise orangée, pastille `#FF7A45 → #E0357A` (da-v2 §2).
//
// Ce module est la SOURCE de la marque pour les cinq surfaces produit : rien n'est
// redessiné ailleurs, et `scripts/build-assets.ts` en dérive les fichiers de
// `assets/brand/` que consomment la vitrine, les favicons et les icônes d'app.
//
// Trois règles de construction, héritées de la planche de branding :
//   1. la pastille est une VRAIE superellipse |x|⁵+|y|⁵=1 (le squircle iOS), jamais un
//      `border-radius` — elle est CALCULÉE, pas stockée (2 ko de contour économisés) ;
//   2. le symbole occupe 80 % du côté de la pastille (86 % sur le favicon, sans quoi il
//      maigrit à 16 px) et il est centré sur elle, pas sur sa boîte ;
//   3. dans un lockup, c'est la HAUTEUR D'X du wordmark qui vaut 40 % de la pastille et
//      qui est centrée sur elle — pas la boîte du mot (elle contient l'ascendante du `f`).

import {
  SYMBOL_PATH,
  SYMBOL_VIEWBOX,
  WORDMARK_PATH,
  WORDMARK_VIEWBOX,
  WORDMARK_X_HEIGHT,
  WORDMARK_X_TOP,
} from './brand-paths.js';
import { brandGradient, brandInk } from './tokens.js';

export {
  SYMBOL_PATH,
  SYMBOL_VIEWBOX,
  WORDMARK_PATH,
  WORDMARK_VIEWBOX,
  WORDMARK_X_HEIGHT,
  WORDMARK_X_TOP,
};

// ---------------------------------------------------------------------------
// Géométrie — tout est exprimé pour une pastille de côté 100, puis mis à l'échelle
// ---------------------------------------------------------------------------

/** Part du côté de la pastille occupée par le symbole. */
const SYMBOL_COVER = 0.8;
/** Idem sur le favicon : « poussé de 6 points » (logos-v3.md §Livrables). */
const FAVICON_COVER = 0.86;
/** Hauteur d'x du wordmark, en part du côté de la pastille, dans un lockup. */
const WORDMARK_X_RATIO = 0.4;
/** Chasse entre la pastille et le mot (lockup horizontal), en part du côté. */
const LOCKUP_GAP = 0.25;
/** Chasse verticale (lockup empilé), en part du côté. */
const LOCKUP_GAP_STACKED = 0.3;

const SYM_W = Number(SYMBOL_VIEWBOX.split(' ')[2]);
const SYM_H = Number(SYMBOL_VIEWBOX.split(' ')[3]);
const WORD_W = Number(WORDMARK_VIEWBOX.split(' ')[2]);
const WORD_H = Number(WORDMARK_VIEWBOX.split(' ')[3]);

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/**
 * Superellipse |x|ⁿ + |y|ⁿ = 1, échantillonnée en polyligne fermée.
 *
 * `n = 5` est le squircle iOS : la courbure y est continue, il n'y a pas de raccord
 * tangent visible entre le droit et l'arc — c'est ce qui la distingue d'un
 * `border-radius`, et c'est ce que la DA exige. À 96 points sur 100 unités, l'écart au
 * tracé exact est très inférieur au pixel : le chemin est mis à l'échelle par le
 * `viewBox`, jamais redécoupé, donc une icône 1024 hérite de la même précision relative.
 */
export function squirclePath(size = 100, n = 5, samples = 96): string {
  const r = size / 2;
  const points: string[] = [];
  for (let i = 0; i < samples; i += 1) {
    const theta = (i / samples) * 2 * Math.PI;
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    const x = r + r * Math.sign(cos) * Math.abs(cos) ** (2 / n);
    const y = r + r * Math.sign(sin) * Math.abs(sin) ** (2 / n);
    points.push(`${Math.round(x * 100) / 100} ${Math.round(y * 100) / 100}`);
  }
  return `M${points.join('L')}Z`;
}

/**
 * Placement du symbole dans une pastille de côté `size` : `cover` du côté en largeur,
 * centré. Le `viewBox` du symbole est CADRÉ SUR SON TRACÉ (`3 15 92.5 70`), pas sur une
 * grille de 100 : c'est ce qui permet de centrer la boîte et d'obtenir un centrage
 * optique — sinon le « 1 », plus haut que la barre, tirerait le dessin vers le bas.
 */
function symbolBox(
  size: number,
  cover: number,
): { x: number; y: number; width: number; height: number } {
  const width = size * cover;
  const height = (width * SYM_H) / SYM_W;
  return {
    x: round((size - width) / 2),
    y: round((size - height) / 2),
    width: round(width),
    height: round(height),
  };
}

function gradientDef(id: string): string {
  const { from, to } = brandGradient.pastille;
  return (
    `<defs><linearGradient id="${id}" x1="0" y1="0" x2="1" y2="1">` +
    `<stop offset="0" stop-color="${from}"/><stop offset="1" stop-color="${to}"/>` +
    '</linearGradient></defs>'
  );
}

/** Pastille dégradée + symbole blanc, sans enveloppe `<svg>` — brique des composeurs. */
function badge(size: number, gradientId: string, cover = SYMBOL_COVER): string {
  const box = symbolBox(size, cover);
  return (
    gradientDef(gradientId) +
    `<path d="${squirclePath(size)}" fill="url(#${gradientId})"/>` +
    `<svg viewBox="${SYMBOL_VIEWBOX}" x="${box.x}" y="${box.y}" width="${box.width}" height="${box.height}" fill="#FFFFFF">` +
    `<path d="${SYMBOL_PATH}"/></svg>`
  );
}

// ---------------------------------------------------------------------------
// API publique
// ---------------------------------------------------------------------------

export interface BrandOptions {
  /**
   * Encre du dessin monochrome (symbole nu, wordmark). `brandInk` par défaut ; sur
   * fond sombre, passer `#FFFFFF`.
   */
  ink?: string;
  /**
   * Suffixe d'`id` du dégradé. Deux logos dans la même page partagent le document :
   * des `id` identiques feraient pointer le second sur le `<defs>` du premier.
   */
  idSuffix?: string;
}

/** Le « +1 » nu, monochrome, un seul `fill` — versions app, mono, gravure. */
export function scorofunSymbol(options: BrandOptions = {}): string {
  const ink = options.ink ?? brandInk;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${SYMBOL_VIEWBOX}" role="img" aria-label="Scorofun">` +
    `<path d="${SYMBOL_PATH}" fill="${ink}"/></svg>`
  );
}

/**
 * La marque réduite : pastille dégradée + « +1 » blanc, carrée. C'est le signe qui
 * tient à 16 px et qui sert partout où le mot ne rentre pas (favicon, en-têtes d'app,
 * coin d'écran du display, icône de store).
 */
export function scorofunMark(options: BrandOptions = {}): string {
  const id = `sf-grad-${options.idSuffix ?? 'mark'}`;
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" role="img" aria-label="Scorofun">' +
    `${badge(100, id)}</svg>`
  );
}

/** Le mot seul, « a — Rondeur », vectorisé — aucune fonte requise. */
export function scorofunWordmark(options: BrandOptions = {}): string {
  const ink = options.ink ?? brandInk;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${WORDMARK_VIEWBOX}" role="img" aria-label="Scorofun">` +
    `<path d="${WORDMARK_PATH}" fill="${ink}"/></svg>`
  );
}

export interface LockupOptions extends BrandOptions {
  /** `dark` encre le mot en blanc, `light` en `brandInk`. */
  theme?: 'dark' | 'light';
  /** `horizontal` (défaut) pour un header, `stacked` pour un logo centré. */
  orientation?: 'horizontal' | 'stacked';
}

/** Dimensions du lockup pour une pastille de côté 100 — utile aux consommateurs SVG. */
export const lockupBox = {
  horizontal: {
    width: round(100 * (1 + LOCKUP_GAP) + WORD_W * WORDMARK_X_RATIO),
    height: 100,
  },
  stacked: {
    width: round(WORD_W * WORDMARK_X_RATIO),
    height: round(100 * (1 + LOCKUP_GAP_STACKED) + WORD_H * WORDMARK_X_RATIO),
  },
} as const;

/**
 * Le lockup complet : pastille + « +1 » + « scorofun ».
 *
 * L'alignement n'est PAS un centrage de boîtes. La hauteur d'x du mot vaut 40 % du côté
 * de la pastille et son milieu tombe sur le milieu de la pastille : l'ascendante du `f`
 * dépasse donc en haut, ce qui est le comportement typographique juste. Centrer la
 * boîte englobante ferait descendre le mot d'un cinquième de sa hauteur d'x.
 */
export function scorofunLockup(options: LockupOptions = {}): string {
  const ink = options.ink ?? (options.theme === 'dark' ? '#FFFFFF' : brandInk);
  const stacked = options.orientation === 'stacked';
  const id = `sf-grad-${options.idSuffix ?? (stacked ? 'stacked' : 'lockup')}`;
  const s = WORDMARK_X_RATIO; // échelle du wordmark pour une pastille de 100
  // Milieu de la bande de hauteur d'x, ramené sur le milieu de la pastille.
  const wordY = round(50 - s * (WORDMARK_X_TOP + WORDMARK_X_HEIGHT / 2));
  const box = stacked ? lockupBox.stacked : lockupBox.horizontal;
  const badgeX = stacked ? round((box.width - 100) / 2) : 0;
  const word =
    `<svg viewBox="${WORDMARK_VIEWBOX}" x="${stacked ? 0 : round(100 * (1 + LOCKUP_GAP))}"` +
    ` y="${stacked ? round(100 * (1 + LOCKUP_GAP_STACKED)) : wordY}"` +
    ` width="${round(WORD_W * s)}" height="${round(WORD_H * s)}"` +
    ` fill="${ink}"><path d="${WORDMARK_PATH}"/></svg>`;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${box.width} ${box.height}" role="img" aria-label="Scorofun">` +
    (badgeX === 0 ? badge(100, id) : `<g transform="translate(${badgeX} 0)">${badge(100, id)}</g>`) +
    word +
    '</svg>'
  );
}

/**
 * Favicon : la pastille seule, symbole poussé à 86 % du côté. À 16 px, le « +1 » à 80 %
 * perd ses contreformes — c'est la seule raison de cette variante.
 */
export function scorofunFavicon(size = 64): string {
  const id = 'sf-grad-favicon';
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" role="img" aria-label="Scorofun">` +
    `${badge(size, id, FAVICON_COVER)}</svg>`
  );
}

/** Icône d'app (stores, apple-touch-icon) : pastille pleine, sans marge de sécurité. */
export function scorofunAppIcon(size = 1024): string {
  const id = 'sf-grad-icon';
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" role="img" aria-label="Scorofun">` +
    `${badge(size, id)}</svg>`
  );
}
